import { notifyAnalytics } from '@/api/analytics/notifyAnalytics'
import { getAuthToken } from '@/api/auth'
import { assertValidEndpoint, BACKEND_URL, type ValidEndpoints } from '@/api/clients/backend-client'

/**
 * Creates a case-insensitive partial match filter for use with PostgREST `ilike`.
 *
 * @param value The string to search for within a field.
 * @returns A QueryValue representing a PostgREST `ilike` filter with wildcards applied.
 *
 * This helper transforms a plain string into a wildcard search pattern:
 *
 * - Input:  "acme"
 * - Output: { op: "ilike", value: "%acme%" }
 * - Query:  ?field=ilike.%acme%
 *
 * Notes:
 * - Uses SQL `ILIKE` semantics via PostgREST (case-insensitive matching).
 * - Automatically wraps the value in `%` wildcards for substring matching.
 * - Intended for filtering text fields only.
 *
 * Example:
 *   { name: contains("acme") } → matches "Acme", "ACME Ltd", "Big Acme Corp"
 */
export const contains = (value: string) => ({
  op: 'ilike',
  value: `%${value}%`,
})

type Primitive = string | number | boolean

type PostgrestOperator = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'ilike' | 'in' | 'is'

type QueryValue =
  | Primitive
  | Primitive[]
  | {
      op?: Exclude<PostgrestOperator, 'in' | 'is'>
      value: Primitive
    }
  | {
      op: 'in'
      value: Primitive[]
    }
  | {
      op: 'is'
      value: null | boolean
    }

type Query = Record<string, QueryValue>

type ApiSuccess<T> = {
  success: true
  hasData: boolean
  statusCode: number
  data?: T | T[]
}

type ApiFailure = {
  success: false
  statusCode: number
  error?: unknown
}

export type ApiResult<T> = ApiSuccess<T> | ApiFailure

/**
 * Builds a URL with query parameters formatted for the essentials backend API
 * using PostgREST-compatible filtering.
 *
 * @param endpoint [ValidEndpoints] The API endpoint to target (e.g. 'users', 'fleets').
 * @param query [Query] Optional query parameters to apply as filters.
 * @returns The constructed URL with encoded query parameters.
 *
 * Query values are translated into PostgREST filter syntax:
 *
 * - Primitive:
 *   { name: "Acme" }
 *   → ?name=eq.Acme
 *
 * - Array (translated to `in` operator):
 *   { id: [1, 2, 3] }
 *   → ?id=in.(1,2,3)
 *
 * - Object with explicit operator:
 *   { age: { op: "gt", value: 30 } }
 *   → ?age=gt.30
 *
 * Operator defaults to `eq` when not provided.
 *
 * Notes:
 * - `undefined` values are ignored.
 * - `null` values are only valid when used with the `is` operator
 *   (e.g. `{ deleted_at: { op: "is", value: null } }` → `is.null`).
 * - All other `null` usages are treated as invalid and ignored.
 *
 * This helper is designed for filtering only; it does not handle
 * advanced PostgREST features such as `select`, `order`, or `or` conditions.
 */
export const buildUrl = (endpoint: ValidEndpoints, query?: Query): string => {
  const url = new URL(assertValidEndpoint(endpoint), BACKEND_URL)

  if (!query) return url.toString()

  Object.entries(query).forEach(([key, raw]) => {
    if (raw === undefined || raw === null) return

    let op = 'eq'
    let value: Primitive | Primitive[] | null

    // object form → explicit operator
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      op = raw.op ?? 'eq'
      value = raw.value
    } else {
      value = raw as Primitive | Primitive[]
    }

    // handle `is` operator (null / boolean)
    if (op === 'is') {
      url.searchParams.set(key, `is.${value}`)
      return
    }

    // prevent invalid null usage
    if (value === null) return

    // array → in.(...)
    if (Array.isArray(value)) {
      const formatted = `in.(${value.join(',')})`
      url.searchParams.set(key, formatted)
      return
    }

    // primitive → eq.value (or other op)
    url.searchParams.set(key, `${op}.${value}`)
  })

  return url.toString()
}

interface FetchHeadersOptions {
  includeAuth?: boolean
  extraHeaders?: Record<string, string>
}

export function buildFetchHeaders({
  includeAuth = true,
  extraHeaders = {},
}: FetchHeadersOptions = {}): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  }

  if (includeAuth) {
    const token = getAuthToken() // your auth store or utility
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  return headers
}

// GET
export async function fetchCollection<TNew, TLegacy = TNew>(
  endpoint: ValidEndpoints,
  query?: Query,
  converter?: (data: TNew) => TLegacy,
): Promise<TLegacy[] | undefined> {
  try {
    const response = await fetch(buildUrl(endpoint, query), {
      method: 'GET',
      headers: buildFetchHeaders(),
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch ${endpoint}: ${response.status} ${response.statusText}`)
    }

    const data = (await response.json()) as TNew[]

    if (!converter) return data as unknown as TLegacy[]

    return data.map(converter)
  } catch (error) {
    if (import.meta.env.DEV) console.error(error)
    notifyAnalytics({
      type: 'log_error',
      data: error,
    })
    return
  }
}

// POST
export async function createItem<TNew, TLegacy = TNew>(
  endpoint: ValidEndpoints,
  body: Record<string, any>,
  converter?: (data: TNew) => TLegacy,
): Promise<ApiResult<TLegacy>> {
  try {
    const response = await fetch(buildUrl(endpoint), {
      method: 'POST',
      headers: buildFetchHeaders({extraHeaders: {Prefer: "return=representation"}}),
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      return {
        success: false,
        statusCode: response.status,
        error: await response.text(),
      }
    }

    const responseText = await response.text()
    const hasData = responseText.length > 0

    if (!hasData) {
      return {
        success: true,
        hasData: false,
        statusCode: response.status,
      }
    }

    const parsedData: TNew | undefined = JSON.parse(responseText)
    return {
      success: true,
      hasData: true,
      statusCode: response.status,
      data:
        converter && parsedData
          ? converter(parsedData)
          : (parsedData as unknown as TLegacy | undefined), // ASSUMES:
      // if no converter is passed it is because it is no longer needed and
      // migration is completed for this collection
    }
  } catch (error) {
    console.error(error)
    return {
      success: false,
      statusCode: 500,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

// PATCH
export async function updateItem<T>({
  endpoint,
  query = undefined,
  body,
  converter = undefined,
}: {
  endpoint: ValidEndpoints
  query?: Query
  body: Record<string, any>
  converter?: (data: any) => T
}): Promise<T | undefined> {
  try {
    const res = await fetch(buildUrl(endpoint, query), {
      method: 'PATCH',
      headers: buildFetchHeaders(),
      body: JSON.stringify(body),
    })
    const data = await res.json()
    return converter ? converter(data) : data
  } catch (error) {
    console.error(error)
    return undefined
  }
}

export async function patchMultiple<T>(
  endpoint: ValidEndpoints,
  ids: string[],
  data: Record<string, any>,
  converter?: (data: any) => T,
): Promise<T[]> {
  const responses = await Promise.all(
    ids.map((id) => updateItem({ endpoint, query: { id }, body: data, converter })),
  )
  return responses.filter((i) => i !== undefined)
}

// DELETE
export async function deleteItem(endpoint: string, id: string): Promise<boolean> {
  try {
    const res = await fetch(`${BACKEND_URL}/${endpoint}/${id}`, {
      method: 'DELETE',
      headers: buildFetchHeaders(),
    })
    return res.ok
  } catch (error) {
    console.error(error)
    return false
  }
}
